// LOGIC PORTED from LC2's TokenVerification (magic-link token verification for both
// regular and SSO login) — same effects/dependencies, same useLazyGetSessionInfoQuery /
// useLazyGetSsoSessionInfoQuery calls. Presentation-wise: instead of silently
// navigate('/login') on any failure, a classifiable failure (expired / already-used link)
// now shows the matching Figma terminal screen (see authErrorHelpers.classifyTokenError);
// anything unrecognized keeps the old "just go back to /login" fallback.
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';

import {getAuthorizedStatus} from '../../app/store/reducers/session';
import {useLazyGetSessionInfoQuery, useLazyGetSsoSessionInfoQuery} from '../../features/loyaltyCloudApi/session';
import {getTokenVerificationLoadingStatus} from '../../app/store/reducers/loading';
import {getCurrentToken} from '../../commons/utils/cookies';
import {classifyTokenError} from './authErrorHelpers';

import AuthShell from './components/AuthShell';
import PageSpinner from './components/PageSpinner';
import LinkStatusCard from './LinkStatusCard';

const TokenVerification = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {search} = useLocation();
    const token = new URLSearchParams(search).get('token');

    const isAuthorized = useSelector(getAuthorizedStatus);
    const tokenStatus = useSelector(getTokenVerificationLoadingStatus);

    const [verifyToken] = useLazyGetSessionInfoQuery();
    const [verifySsoToken] = useLazyGetSsoSessionInfoQuery();
    const [isVerifying, setIsVerifying] = useState(false);
    const [linkFailure, setLinkFailure] = useState(null); // 'expired' | 'already-used' | null

    useEffect(() => {
        const verifyTokenAndInitialize = async () => {
            const isRegularLogin = location.pathname === '/verify_login';
            const isSsoLogin = location.pathname === '/verify_sso_login';

            if (token && !tokenStatus.isLoading && !tokenStatus.isError && !tokenStatus.isSuccess) {
                try {
                    if (isRegularLogin) {
                        await verifyToken(token).unwrap();
                    } else if (isSsoLogin) {
                        await verifySsoToken(token).unwrap();
                    }
                    navigate('/');
                } catch (error) {
                    console.error('Token verification failed:', error);
                    const classified = classifyTokenError(error);
                    if (classified) {
                        setLinkFailure(classified);
                    } else {
                        navigate('/login');
                    }
                } finally {
                    setIsVerifying(false);
                }
            }
        };

        verifyTokenAndInitialize();
    }, [token, verifyToken, verifySsoToken, location.pathname, tokenStatus, navigate, isVerifying]);

    useEffect(() => {
        const checkAuthorization = async () => {
            if (!isVerifying && !linkFailure) {
                const currentToken = await getCurrentToken();
                if (isAuthorized && currentToken) {
                    navigate('/');
                } else if (tokenStatus.isError || (!isAuthorized && !currentToken && !token)) {
                    navigate('/login');
                }
            }
        };

        checkAuthorization();
    }, [isAuthorized, navigate, tokenStatus.isError, isVerifying, token, linkFailure]);

    if (linkFailure) {
        return (
            <AuthShell>
                <LinkStatusCard variant={linkFailure} onRequestNewLink={() => navigate('/login')} onBackToLogin={() => navigate('/login')} />
            </AuthShell>
        );
    }

    return <PageSpinner />;
};

export default TokenVerification;
